import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { universityApi } from '../../api/services';
import { MultiSelectCombobox } from '../../components/Form/MultiSelectCombobox';
import { useLanguage } from '../../contexts/LanguageContext';
import { cn } from '../../lib/cn';
import { formatNumber } from '../../lib/format';
import type { AcademicStage, PathDocumentKey, PathProfile } from '../../types';

const STAGES: AcademicStage[] = [
  'exploring', 'high_school', 'bsc_student', 'bsc_graduate', 'msc_student',
  'msc_graduate', 'phd_applicant', 'language_course', 'returning_after_rejection',
];
const DOCS: PathDocumentKey[] = ['transcript', 'degree_certificate', 'cv', 'sop', 'recommendation_letters'];
const thisYear = new Date().getFullYear();

const field = 'w-full rounded-lg border border-border bg-surface px-3.5 py-2.5 text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary';
const labelText = 'space-y-1.5 text-sm font-medium text-text-secondary';

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <fieldset className="space-y-4 rounded-xl border border-border bg-surface p-5">
      <legend className="sr-only">{title}</legend>
      <div>
        <h2 className="text-base font-semibold text-text-primary" aria-hidden="true">{title}</h2>
        {hint && <p className="mt-1 max-w-[65ch] text-sm text-text-secondary">{hint}</p>}
      </div>
      {children}
    </fieldset>
  );
}

function Choice<T extends string>({ value, options, onChange, label }: { value: T | null | undefined; options: T[]; onChange: (v: T) => void; label: (v: T) => string }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => (
        <label
          key={option}
          className={cn(
            'cursor-pointer rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-primary',
            value === option ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-border text-text-secondary hover:bg-elevated'
          )}
        >
          <input type="radio" className="sr-only" checked={value === option} onChange={() => onChange(option)} />
          {label(option)}
        </label>
      ))}
    </div>
  );
}

export function PathSetupForm({
  initial,
  saving,
  onSubmit,
  onCancel,
}: {
  initial: Partial<PathProfile> | null;
  saving: boolean;
  onSubmit: (profile: PathProfile) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [p, setP] = useState<Partial<PathProfile>>(() => ({
    language_test_status: 'not_started',
    budget_currency: language === 'fa' ? 'IRT' : 'EUR',
    funding_source: 'unknown',
    payment_access: 'unknown',
    translation_attestation_status: 'not_started',
    documents: {},
    target_countries: [],
    ...initial,
  }));
  const [countries, setCountries] = useState<Array<{ value: string; label: string; count: number }>>([]);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    universityApi
      .getCountries()
      .then((rows) => setCountries(rows.map((r) => ({ value: r.country, label: r.country, count: r.count }))))
      .catch(() => setCountries([]));
  }, []);

  const set = <K extends keyof PathProfile>(key: K, value: PathProfile[K]) => setP((prev) => ({ ...prev, [key]: value }));
  const num = (value: string) => (value === '' ? null : Number(value));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!p.academic_stage) {
      setShowErrors(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // Drop empty values so the API applies its defaults.
    const clean = Object.fromEntries(Object.entries(p).filter(([, v]) => v !== '' && v !== undefined)) as unknown as PathProfile;
    onSubmit(clean);
  };

  const showLanguageDetails = p.language_test_status && p.language_test_status !== 'not_started';

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <Section title={t('path.form.stage.title')} hint={t('path.form.stage.hint')}>
        <Choice value={p.academic_stage} options={STAGES} onChange={(v) => set('academic_stage', v)} label={(v) => t(`path.stage.${v}`)} />
        {showErrors && !p.academic_stage && <p className="text-xs text-status-danger">{t('path.form.stage.required')}</p>}
      </Section>

      <Section title={t('path.form.goal.title')}>
        <div className="space-y-1.5">
          <span className="text-sm font-medium text-text-secondary">{t('path.form.goal.degree')}</span>
          <Choice
            value={p.target_degree}
            options={['master', 'phd', 'bachelor', 'language_course'] as Array<NonNullable<PathProfile['target_degree']>>}
            onChange={(v) => set('target_degree', v)}
            label={(v) => t(`path.degree.${v}`)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className={labelText}>
            <span>{t('path.form.goal.field')}</span>
            <input value={p.field_of_study ?? ''} onChange={(e) => set('field_of_study', e.target.value)} maxLength={200} className={field} placeholder={t('path.form.goal.fieldPlaceholder')} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className={labelText}>
              <span>{t('path.form.goal.intakeYear')}</span>
              <select value={p.target_intake_year ?? ''} onChange={(e) => set('target_intake_year', num(e.target.value))} className={field}>
                <option value="">{t('path.form.notSure')}</option>
                {[0, 1, 2, 3].map((d) => <option key={d} value={thisYear + d}>{formatNumber(thisYear + d, language, { useGrouping: false })}</option>)}
              </select>
            </label>
            <label className={labelText}>
              <span>{t('path.form.goal.term')}</span>
              <select value={p.target_intake_term ?? ''} onChange={(e) => set('target_intake_term', (e.target.value || null) as PathProfile['target_intake_term'])} className={field}>
                <option value="">{t('path.form.notSure')}</option>
                <option value="fall">{t('path.form.goal.fall')}</option>
                <option value="spring">{t('path.form.goal.spring')}</option>
              </select>
            </label>
          </div>
        </div>
        <MultiSelectCombobox
          label={t('path.form.goal.countries')}
          options={countries}
          selected={p.target_countries ?? []}
          onChange={(selected) => set('target_countries', selected)}
          placeholder={t('path.form.goal.countriesPlaceholder')}
          maxSelections={12}
        />
      </Section>

      <Section title={t('path.form.language.title')}>
        <Choice
          value={p.language_test_status}
          options={['not_started', 'booked', 'taken_waiting', 'score_ready', 'expired'] as Array<NonNullable<PathProfile['language_test_status']>>}
          onChange={(v) => set('language_test_status', v)}
          label={(v) => t(`path.languageStatus.${v}`)}
        />
        {showLanguageDetails && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className={labelText}>
              <span>{t('path.form.language.test')}</span>
              <select value={p.language_test_type ?? ''} onChange={(e) => set('language_test_type', (e.target.value || null) as PathProfile['language_test_type'])} className={field}>
                <option value="">—</option>
                {['ielts', 'toefl_ibt', 'pte', 'duolingo', 'goethe', 'testdaf', 'tcf', 'delf', 'other'].map((x) => (
                  <option key={x} value={x}>{t(`languageTests.tests.${x}`)}</option>
                ))}
              </select>
            </label>
            {p.language_test_status === 'score_ready' && (
              <label className={labelText}>
                <span>{t('path.form.language.score')}</span>
                <input inputMode="decimal" value={p.language_score ?? ''} onChange={(e) => set('language_score', num(e.target.value.replace(/[^\d.]/g, '')))} className={field} />
              </label>
            )}
            <label className={labelText}>
              <span>{t('path.form.language.date')}</span>
              <input type="date" value={p.language_test_date ?? ''} onChange={(e) => set('language_test_date', e.target.value || null)} className={field} />
            </label>
            <label className={labelText}>
              <span>{t('path.form.language.city')}</span>
              <input value={p.city_for_tests ?? ''} onChange={(e) => set('city_for_tests', e.target.value)} maxLength={80} className={field} />
            </label>
          </div>
        )}
      </Section>

      <Section title={t('path.form.money.title')} hint={t('path.form.money.hint')}>
        <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
          <label className={labelText}>
            <span>{t('path.form.money.budget')}</span>
            <input inputMode="numeric" value={p.budget_amount ?? ''} onChange={(e) => set('budget_amount', num(e.target.value.replace(/[^\d]/g, '')))} className={field} />
          </label>
          <label className={labelText}>
            <span>{t('path.form.money.currency')}</span>
            <select value={p.budget_currency} onChange={(e) => set('budget_currency', e.target.value as PathProfile['budget_currency'])} className={field}>
              {['IRT', 'EUR', 'USD', 'CAD', 'GBP', 'SEK', 'DKK'].map((c) => <option key={c} value={c}>{t(`path.currency.${c}`, c)}</option>)}
            </select>
          </label>
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium text-text-secondary">{t('path.form.money.funding')}</span>
          <Choice
            value={p.funding_source}
            options={['family', 'savings', 'scholarship_required', 'part_time_needed', 'unknown'] as Array<NonNullable<PathProfile['funding_source']>>}
            onChange={(v) => set('funding_source', v)}
            label={(v) => t(`path.funding.${v}`)}
          />
        </div>
      </Section>

      <Section title={t('path.form.documents.title')} hint={t('path.form.documents.hint')}>
        <div className="flex flex-wrap gap-2">
          {DOCS.map((doc) => (
            <label key={doc} className={cn('flex cursor-pointer items-center gap-2 rounded-lg border px-3.5 py-2 text-sm', p.documents?.[doc] ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-border text-text-secondary hover:bg-elevated')}>
              <input type="checkbox" checked={Boolean(p.documents?.[doc])} onChange={(e) => set('documents', { ...p.documents, [doc]: e.target.checked })} className="h-4 w-4 rounded border-border text-brand-primary focus:ring-brand-primary" />
              {t(`path.doc.${doc}`)}
            </label>
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className={labelText}>
            <span>{t('path.form.documents.attestation')}</span>
            <select value={p.translation_attestation_status} onChange={(e) => set('translation_attestation_status', e.target.value as PathProfile['translation_attestation_status'])} className={field}>
              {['not_started', 'in_progress', 'done'].map((s) => <option key={s} value={s}>{t(`path.attestation.${s}`)}</option>)}
            </select>
          </label>
          <label className={labelText}>
            <span>{t('path.form.documents.passport')}</span>
            <select value={p.passport_status ?? ''} onChange={(e) => set('passport_status', (e.target.value || null) as PathProfile['passport_status'])} className={field}>
              <option value="">{t('path.form.notSure')}</option>
              {['valid', 'expiring_soon', 'applied', 'none'].map((s) => <option key={s} value={s}>{t(`path.passport.${s}`)}</option>)}
            </select>
          </label>
        </div>
      </Section>

      <Section title={t('path.form.iran.title')} hint={t('path.form.iran.hint')}>
        <div className="grid gap-4 sm:grid-cols-3">
          <label className={labelText}>
            <span>{t('path.form.iran.military')}</span>
            <select value={p.military_service_status ?? ''} onChange={(e) => set('military_service_status', (e.target.value || null) as PathProfile['military_service_status'])} className={field}>
              <option value="">{t('path.form.preferNotToSay')}</option>
              {['not_applicable', 'exempt', 'completed', 'student_deferral', 'unresolved'].map((s) => <option key={s} value={s}>{t(`path.military.${s}`)}</option>)}
            </select>
          </label>
          <label className={labelText}>
            <span>{t('path.form.iran.payment')}</span>
            <select value={p.payment_access} onChange={(e) => set('payment_access', e.target.value as PathProfile['payment_access'])} className={field}>
              {['unknown', 'has_foreign_card', 'family_abroad', 'needs_intermediary'].map((s) => <option key={s} value={s}>{t(`path.payment.${s}`)}</option>)}
            </select>
          </label>
          <label className={labelText}>
            <span>{t('path.form.iran.embassy')}</span>
            <select
              value={p.can_travel_for_embassy == null ? '' : String(p.can_travel_for_embassy)}
              onChange={(e) => set('can_travel_for_embassy', e.target.value === '' ? null : e.target.value === 'true')}
              className={field}
            >
              <option value="">{t('path.form.notSure')}</option>
              <option value="true">{t('path.form.yes')}</option>
              <option value="false">{t('path.form.no')}</option>
            </select>
          </label>
        </div>
      </Section>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {onCancel && (
          <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2.5 text-sm font-medium text-text-secondary hover:bg-elevated">
            {t('common.cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-brand-primary px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-secondary disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
        >
          {saving ? t('common.saving') : t('path.form.submit')}
        </button>
      </div>
    </form>
  );
}
